import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  CalendarDaysIcon,
  ClipboardDocumentListIcon,
  CpuChipIcon,
  BellAlertIcon,
  SunIcon,
} from '@heroicons/react/24/outline';
import { persistHomePrimaryFarmId } from '../../../utils/homePrimaryFarmPrefs';
import { buildBusinessContextPath, EMPTY_COPY } from '../../../utils/businessContextNav';

const QUICK_ACTIONS = [
  { label: '种植计划', path: '/production/crop/planted', Icon: CalendarDaysIcon },
  { label: '农事任务', path: '/production/field-work/plans', Icon: ClipboardDocumentListIcon },
  { label: '智能设备', path: '/production/land/smart-device', Icon: CpuChipIcon },
  { label: '预警管理', path: '/production/field-work/warning-management', Icon: BellAlertIcon },
  { label: '智慧驾驶舱', path: '/smart-farm-cockpit', Icon: SunIcon },
];

const ZERO_KPIS = [
  { label: '地块数量', value: '0块', foot: EMPTY_COPY.NO_RECORD, tone: 'muted', key: 'plots' },
  { label: '设备数量', value: '0台', foot: EMPTY_COPY.NOT_CONNECTED, tone: 'muted', key: 'devices' },
  { label: '在线设备', value: '0台', foot: EMPTY_COPY.NOT_CONNECTED, tone: 'muted', key: 'online' },
  { label: '农事任务', value: '0条', foot: EMPTY_COPY.NO_RECORD, tone: 'muted', key: 'tasks' },
  { label: '异常告警', value: '0条', foot: EMPTY_COPY.NO_RECORD, tone: 'ok', key: 'risk' },
  { label: '农场数', value: '0个', foot: EMPTY_COPY.NO_RECORD, tone: 'muted', key: 'farms' },
];

const MyFarmSection = ({ farmPreview, loading, onPrimaryFarmChange, farmId = '', landId = '', deviceId = '' }) => {
  const navigate = useNavigate();
  const hero = farmPreview?.hero || null;
  const kpis = Array.isArray(farmPreview?.kpis) ? farmPreview.kpis : [];
  const isDemo = farmPreview?.showDemoFarmPreview === true;
  const hasFarm = Boolean(hero && !hero.isDemo);
  const farmOptions = Array.isArray(farmPreview?.farmOptions) ? farmPreview.farmOptions : [];
  const preferredFarmId = farmPreview?.preferredFarmId || hero?.farmId || farmId || '';
  const activeFarmId = String(preferredFarmId || farmId || '').trim();
  const ctx = {
    farmId: activeFarmId,
    landId: landId || '',
    deviceId: deviceId || '',
  };

  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const pickerRef = useRef(null);

  const displayKpis = kpis.length > 0 ? kpis : ZERO_KPIS;

  const toneColors = {
    ok: 'text-green-600',
    info: 'text-blue-600',
    warn: 'text-orange-500',
    muted: 'text-gray-500',
  };

  const stageText = hero?.stageHighlight || (hasFarm ? '查看种植计划与农事安排' : '暂无农场档案');

  useEffect(() => {
    if (!pickerOpen) return undefined;
    const onDoc = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setPickerOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setPickerOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [pickerOpen]);

  const handlePickFarm = async (farmId) => {
    const id = String(farmId || '').trim();
    if (!id || id === preferredFarmId || saving) {
      setPickerOpen(false);
      return;
    }
    setSaving(true);
    try {
      await persistHomePrimaryFarmId(id);
      if (typeof onPrimaryFarmChange === 'function') {
        await onPrimaryFarmChange(id);
      }
      setPickerOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const handleKpiNavigate = (key) => {
    const map = {
      plots: '/production/land',
      farms: '/production/land',
      devices: '/production/land/smart-device',
      online: '/production/land/smart-device',
      tasks: '/production/field-work/plans',
      risk: '/production/field-work/warning-management',
    };
    const path = map[key];
    if (path) navigate(buildBusinessContextPath(path, ctx));
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden">
      <div className="px-4 py-3 flex items-center justify-between shrink-0">
        <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
          <HomeIcon className="h-4 w-4 text-green-600" aria-hidden />
          <span>我的农场</span>
        </h3>
        <div className="relative flex items-center gap-3" ref={pickerRef}>
          {!isDemo && farmOptions.length > 0 ? (
            <>
              <button
                type="button"
                className="inline-flex h-7 w-7 items-center justify-center rounded border border-gray-200 text-gray-600 hover:border-green-400 hover:text-green-700 hover:bg-green-50 transition-colors"
                title="设置优先显示的主农场"
                aria-label="设置优先显示的主农场"
                aria-expanded={pickerOpen}
                onClick={() => setPickerOpen((v) => !v)}
              >
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z" />
                  <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9c.3.6.9 1 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
                </svg>
              </button>
              {pickerOpen ? (
                <div
                  className="absolute right-0 top-full z-20 mt-1 w-52 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
                  role="dialog"
                  aria-label="选择优先主农场"
                >
                  <div className="border-b border-gray-100 px-3 py-2 text-xs font-medium text-gray-600">
                    优先显示的主农场
                  </div>
                  <ul className="max-h-56 overflow-y-auto py-1">
                    {farmOptions.map((f) => {
                      const active = String(f.id) === String(preferredFarmId);
                      return (
                        <li key={f.id}>
                          <button
                            type="button"
                            disabled={saving}
                            className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-green-50 disabled:opacity-60 ${
                              active ? 'bg-green-50 text-green-800 font-semibold' : 'text-gray-700'
                            }`}
                            onClick={() => handlePickFarm(f.id)}
                          >
                            <span className="truncate">{f.name}</span>
                            {active ? (
                              <span className="shrink-0 rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-700">
                                主农场
                              </span>
                            ) : null}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}
            </>
          ) : null}
          <Link
            to={buildBusinessContextPath('/production/land', ctx)}
            className="text-sm text-gray-600 hover:text-green-700 transition-colors"
          >
            全部农场
          </Link>
        </div>
      </div>

      <div className="p-4 flex flex-col gap-4">
        {loading ? (
          <div className="py-10 flex items-center justify-center">
            <div className="animate-spin h-6 w-6 border-2 border-green-200 border-t-green-600 rounded-full" />
          </div>
        ) : !hasFarm && !isDemo ? (
          <>
            <div className="py-8 flex flex-col items-center justify-center text-center gap-3">
              <SunIcon className="h-10 w-10 text-gray-400" aria-hidden />
              <p className="text-sm text-gray-600">{EMPTY_COPY.NO_RECORD}</p>
              <button
                type="button"
                onClick={() => navigate('/production/land')}
                className="mt-1 px-4 py-2 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
              >
                去创建农场
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {displayKpis.map((item, idx) => (
                <button
                  type="button"
                  key={item.key || idx}
                  onClick={() => handleKpiNavigate(item.key)}
                  className="rounded-lg border border-gray-200 p-3 flex flex-col gap-1 text-left hover:border-green-300 transition-colors"
                >
                  <span className="text-xs text-gray-600 truncate">{item.label}</span>
                  <span className="text-xl font-bold text-gray-800 leading-tight">{item.value}</span>
                  <span className={`text-xs ${toneColors[item.tone] || toneColors.muted}`}>{item.foot}</span>
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1">
              {QUICK_ACTIONS.map((item) => {
                const Icon = item.Icon;
                return (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => navigate(buildBusinessContextPath(item.path, ctx))}
                  className="flex flex-col items-center gap-1 py-2 rounded-lg hover:bg-green-50 transition-colors min-w-0"
                >
                  <Icon className="h-5 w-5 text-green-700" aria-hidden />
                  <span className="text-xs text-gray-600 leading-tight text-center truncate w-full px-0.5">{item.label}</span>
                </button>
                );
              })}
            </div>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => navigate(buildBusinessContextPath('/production/land', ctx))}
              className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-green-400 hover:bg-green-50 transition-all text-left min-w-0"
            >
              <div className="w-12 h-12 rounded-lg overflow-hidden bg-green-600 shrink-0 flex items-center justify-center">
                {hero?.coverUrl ? (
                  <img src={hero.coverUrl} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                ) : (
                  <SunIcon className="h-6 w-6 text-white" aria-hidden />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-800 truncate">{hero?.name || '我的农场'}</span>
                  <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded font-medium shrink-0">
                    {hero?.badgeText || '主农场'}
                  </span>
                </div>
                <div className="text-sm text-gray-600 mt-1">{hero?.metaLine || '—'}</div>
                {hero?.showStageProgress ? (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs text-gray-600">{hero?.stagePrefix || '当前阶段：'}{hero?.stageHighlight || ''}</span>
                    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden max-w-[100px]">
                      <div className="h-full bg-green-500 rounded-full" style={{ width: `${hero?.progressPct || 0}%` }} />
                    </div>
                    <span className="text-xs text-green-700 font-medium">{hero?.progressPct || 0}%</span>
                  </div>
                ) : null}
              </div>
            </button>

            <div className="grid grid-cols-2 gap-3">
              {displayKpis.map((item, idx) => (
                <button
                  type="button"
                  key={item.key || idx}
                  onClick={() => handleKpiNavigate(item.key)}
                  className="rounded-lg border border-gray-200 p-3 flex flex-col gap-1 hover:border-green-300 transition-colors text-left"
                >
                  <span className="text-xs text-gray-600 truncate">{item.label}</span>
                  <span className="text-xl font-bold text-gray-800 leading-tight">{item.value}</span>
                  <span className={`text-xs ${toneColors[item.tone] || toneColors.muted}`}>{item.foot}</span>
                </button>
              ))}
            </div>

            <div className="rounded-lg bg-blue-50 border border-blue-100 px-3 py-2">
              <div className="text-xs text-blue-700 mb-0.5">{hero?.stagePrefix || '农事提示'}</div>
              <div className="text-sm text-blue-900 font-medium">{stageText}</div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1">
              {QUICK_ACTIONS.map((item) => {
                const Icon = item.Icon;
                return (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => navigate(buildBusinessContextPath(item.path, ctx))}
                  className="flex flex-col items-center gap-1 py-2 rounded-lg hover:bg-green-50 transition-colors min-w-0"
                >
                  <Icon className="h-5 w-5 text-green-700" aria-hidden />
                  <span className="text-xs text-gray-600 leading-tight text-center truncate w-full px-0.5">{item.label}</span>
                </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MyFarmSection;
